import type { CommandGroupSource } from "@omegagrid/commands";
import type { AccountingSearchEntity } from "@reporting/rp-types/accounting";

/** the group of the companies the user entered most recently, listed before the accounting groups */
export const RECENT_GROUP_ID = 'g-recent';

export const accountingGroupId = (idGroup: number) => `g-${idGroup}`;

export type AccountingCommandProps = {
	accounting: number;
	/** when the user last entered the company; the commands of the recent group only */
	date_visited?: string;
};

/**
 * Turns the rows of accounting/search - already in display order: the recent companies (without a
 * term only), the group of the current accounting, the rest - into command groups: one for the recent
 * companies, then one per accounting group in order of first appearance. A recently used company is a
 * command in both.
 */
export function buildAccountingGroups(
	rows: AccountingSearchEntity[],
	recentGroupName: string,
	exec: (accounting: number) => void | Promise<void>
): CommandGroupSource[] {
	const groups: CommandGroupSource[] = [];
	const byId = new Map<string, CommandGroupSource>();

	for (const row of rows) {
		const recent = row.date_visited != null;
		const id = recent ? RECENT_GROUP_ID : accountingGroupId(row.id_accounting_group);

		let group = byId.get(id);
		if (!group) {
			group = {
				id,
				name: recent ? recentGroupName : `${row.id_accounting_group} ${row.group_name}`,
				commands: []
			};
			byId.set(id, group);
			groups.push(group);
		}

		const props: AccountingCommandProps = {accounting: row.accounting};
		if (recent) props.date_visited = row.date_visited;

		group.commands.push({
			id: `${recent ? 'r' : 'a'}-${row.accounting.toString().replace('-', 'n')}`,
			name: `${row.accounting} ${row.company}`,
			group: id,
			props,
			exec: () => exec(row.accounting)
		});
	}

	return groups;
}
